import { isNotDefined } from '@sgme/fp';
import { format } from 'date-fns';

import { monitorTimeout } from '@/api/monitorTimeout.ts';
import type { DealRejected, DealReply, FillReport } from '@/api/signalR/messages/execution.ts';
import { getSignalRConnector } from '@/api/signalR/SignalRConnector.ts';
import type { WebapiEndpointBuilder } from '@/api/webapi.ts';
import { buildHandleLateFillReport } from '@/store/growl/lateFillReport.ts';
import { getExecution, GrowlSlice, type GrowUpdate } from '@/store/growl/slice.ts';
import { updateGrowlApiErrorThunk } from '@/store/growl/updateGrowlApiErrorThunk.ts';
import { whenGrowlClosed } from '@/store/growl/whenGrowlClosed.ts';
import type { AppDispatch, AppState, GetAppState } from '@/store/store.ts';
import { swapApi } from '@/store/swap/api/index.ts';
import { closeSwapRfs } from '@/store/swap/api/requestSwapRfs.ts';
import { selectSwapTile } from '@/store/swap/selectors/tile.ts';
import {
  swapExecutionPending,
  swapExecutionRejected,
  swapExecutionReseted,
  swapExecutionSuccess,
  swapRejectedExecutionReseted,
} from '@/store/swap/slice.ts';
import { getSwapTradeCaptureQueue } from '@/store/swap/swapTradeCaptureQueue.ts';
import type { Way } from '@/types/common.ts';
import type { ISODate, ISODateTime } from '@/types/date.ts';
import type { CurrencyIndex } from '@/utils/currencies/types.ts';
import type { Timestamp } from '@/utils/date.ts';
import { SECOND_IN_MILLIS } from '@/utils/time.ts';
import { toBase64 } from '@/utils/toBase64.ts';

export type ExecuteSwapRfsParams = {
  tileId: string;

  rfsId: string;
  quoteId: string;
  signalRConnectionId: string;
  clientId: number;
  notificationEmails: string;

  isNonDeliverable: boolean; // true for NDS

  type: 'standard' | 'rollover' | 'predeliver';

  way: Way;
  currency1: string;
  currency2: string;
  currencyPairPrecision: number;

  swapPoints: number;

  spot: number;

  nearNotional: number;
  nearCurrencyIndex: CurrencyIndex;
  nearExpiryDate: ISODate;
  nearPrice: number;
  nearPoints: number;
  nearSalesAllInRate: number;

  farNotional: number;
  farCurrencyIndex: CurrencyIndex;
  farExpiryDate: ISODate;
  farPrice: number;
  farPointsWithoutMargin: number;
  farPointsWithMargin: number;
  farSalesAllInRate: number;

  userLocalDateTime: Timestamp;
  lastQuoteFromBackTimestamp: Timestamp;
  lastQuoteReceivedByFrontTimestamp: Timestamp;
  executionClickByFrontTimestamp: Timestamp;
};

export type ExecuteSwapRfsBody = {
  rfsId: string;
  quoteId: string;
  replyToStream: string;
  email64NotificationList: string;

  way: Way;
  spot: number;
  nearPrice: number;
  farPrice: number;
  nearPoints: number;
  farPointsWithoutMargin: number;
  farPointsWithMargin: number;
  nearSalesAllInRate: number;
  farSalesAllInRate: number;

  userLocalDateTime: ISODateTime;
  lastQuoteFromBackTimestamp: Timestamp;
  lastQuoteReceivedByFrontTimestamp: Timestamp;
  executionClickByFrontTimestamp: Timestamp;
};

export type ExecuteSwapRfsResponse = {};

export const executeSwapRfs = (builder: WebapiEndpointBuilder) => {
  return builder.mutation<ExecuteSwapRfsResponse, ExecuteSwapRfsParams>({
    query: (params) => {
      const body: ExecuteSwapRfsBody = {
        rfsId: params.rfsId,
        quoteId: params.quoteId,
        replyToStream: params.signalRConnectionId,
        email64NotificationList: toBase64(params.notificationEmails),

        way: params.way,
        spot: params.spot,
        nearPrice: params.nearPrice,
        farPrice: params.farPrice,
        nearPoints: params.nearPoints,
        farPointsWithoutMargin: params.farPointsWithoutMargin,
        farPointsWithMargin: params.farPointsWithMargin,
        nearSalesAllInRate: params.nearSalesAllInRate,
        farSalesAllInRate: params.farSalesAllInRate,

        userLocalDateTime: format(params.userLocalDateTime, "yyyy-MM-dd'T'HH:mm:ss") as ISODateTime,
        lastQuoteFromBackTimestamp: params.lastQuoteFromBackTimestamp,
        lastQuoteReceivedByFrontTimestamp: params.lastQuoteReceivedByFrontTimestamp,
        executionClickByFrontTimestamp: params.executionClickByFrontTimestamp,
      };

      return {
        url: '/fxSwap/rfs/execute',
        method: 'POST',
        // headers: { 'X-Correlation-ID': body.executionId },
        body,
      };
    },

    async onQueryStarted(request, { queryFulfilled, getState, dispatch }) {
      const { tileId, rfsId } = request;

      const stopMonitor = monitorTimeout(rfsId, dispatch, {
        onExecFail: () => {
          dispatchSwapExecutionRejected(tileId, rfsId, dispatch);
        },
        onExecSuccess: () => {
          dispatchSwapExecutionSuccess(tileId, rfsId, getState as GetAppState, dispatch);
        },
        getPendingExecutionDetails: () => {
          const execution = getExecution(getState() as AppState, rfsId);
          if (
            isNotDefined(execution) ||
            execution.type !== 'swapDealExecution' ||
            execution.data.status !== 'pending'
          ) {
            return undefined;
          }

          return {
            clientId: execution.data.data.accountId,
            currencyPair: `${execution.data.data.currency1}/${execution.data.data.currency2}`,
            productType: execution.data.productName,
            tradeDate: execution.data.data.tradeDate,
          };
        },
      });

      try {
        await queryFulfilled;
      } catch (error) {
        stopMonitor();
        dispatch(updateGrowlApiErrorThunk({ growlId: rfsId, error }));
        dispatchSwapExecutionRejected(tileId, rfsId, dispatch);
      }
    },

    async onCacheEntryAdded(params, { cacheEntryRemoved, getState, dispatch }) {
      const handleReceiveDealReply = buildHandleReceiveDealReply(params, getState as GetAppState, dispatch);
      const handleReceiveDealRejected = buildHandleReceiveDealRejected(params, dispatch);
      const handleReceiveFillReport = buildHandleReceiveFillReport(params, dispatch);

      const signalRConnector = getSignalRConnector();

      signalRConnector.on('ReceiveDealReply', handleReceiveDealReply);
      signalRConnector.on('ReceiveDealRejected', handleReceiveDealRejected);
      signalRConnector.on('ReceiveFillReport', handleReceiveFillReport);

      dispatch(
        GrowlSlice.actions.create({
          growlId: params.rfsId,
          growl: {
            type: 'swapDealExecution',
            createdAt: Date.now(),
            options: { closeable: false },
            data: {
              tileId: params.tileId,
              productName: 'FxSwap',
              status: 'pending',
              data: {
                isNonDeliverable: params.isNonDeliverable,

                type: params.type,

                way: params.way,
                currency1: params.currency1,
                currency2: params.currency2,
                currencyPairPrecision: params.currencyPairPrecision,

                accountId: params.clientId,

                swapPoints: params.swapPoints,
                tradeDate: format(params.userLocalDateTime, "yyyy-MM-dd'T'HH:mm:ss") as ISODateTime,

                nearLeg: {
                  notional: params.nearNotional,
                  currencyIndex: params.nearCurrencyIndex,
                  price: params.nearPrice,
                  date: params.nearExpiryDate,
                },

                farLeg: {
                  notional: params.farNotional,
                  currencyIndex: params.farCurrencyIndex,
                  price: params.farPrice,
                  date: params.farExpiryDate,
                },
              },
            },
          },
        }),
      );

      dispatch(swapExecutionPending({ tileId: params.tileId, executionId: params.rfsId }));

      // wait for the execution request done
      await cacheEntryRemoved;

      // stop the RFS stream (signalR listeners, timeouts, etc)
      closeSwapRfs(params.tileId, params.rfsId, dispatch);

      signalRConnector.off('ReceiveDealReply', handleReceiveDealReply);
      signalRConnector.off('ReceiveDealRejected', handleReceiveDealRejected);
      signalRConnector.off('ReceiveFillReport', handleReceiveFillReport);

      // the back office can send the trade id long after the execution request is over
      // (NOT_YET_INTEGRATED): keep listening, but only on behalf of the growl, and only as long as
      // the user keeps it open
      const handleLateFillReport = buildHandleLateFillReport(params.rfsId, dispatch);
      signalRConnector.on('ReceiveFillReport', handleLateFillReport);

      await whenGrowlClosed(params.rfsId, getState as GetAppState);

      signalRConnector.off('ReceiveFillReport', handleLateFillReport);
    },
  });
};

// ███████╗██╗ ██████╗ ███╗   ██╗ █████╗ ██╗         ██████╗
// ██╔════╝██║██╔════╝ ████╗  ██║██╔══██╗██║         ██╔══██╗
// ███████╗██║██║  ███╗██╔██╗ ██║███████║██║         ██████╔╝
// ╚════██║██║██║   ██║██║╚██╗██║██╔══██║██║         ██╔══██╗
// ███████║██║╚██████╔╝██║ ╚████║██║  ██║███████╗    ██║  ██║
// ╚══════╝╚═╝ ╚═════╝ ╚═╝  ╚═══╝╚═╝  ╚═╝╚══════╝    ╚═╝  ╚═╝

// SEE: fx-web/app/src/Workers/SignalR/signalRStream.ts
const TIMEOUT_TOTAL_MILLIS = 20 * SECOND_IN_MILLIS;

const buildHandleReceiveDealReply = (params: ExecuteSwapRfsParams, getState: GetAppState, dispatch: AppDispatch) => {
  return (dealReply: DealReply) => {
    if (dealReply.Execute.RfsId !== params.rfsId) {
      return;
    }

    dispatchSwapExecutionSuccess(params.tileId, dealReply.Execute.RfsId, getState, dispatch);

    // FillReport is expected after DealReply and will provide trade IDs + close listeners.
    // If no FillReport arrives within TIMEOUT_TOTAL_MILLIS, close listeners as fallback.
    setTimeout(() => {
      const execution = getExecution(getState(), params.rfsId);

      if (isNotDefined(execution) || execution.type !== 'swapDealExecution' || execution.data.status !== 'integrated') {
        return;
      }

      dispatch(swapApi.internalActions.removeMutationResult({ fixedCacheKey: params.rfsId }));
    }, TIMEOUT_TOTAL_MILLIS);
  };
};

const dispatchSwapExecutionSuccess = (tileId: string, rfsId: string, getState: GetAppState, dispatch: AppDispatch) => {
  dispatch(
    GrowlSlice.actions.update({
      growlId: rfsId,
      data: { status: 'integrated' },
      options: {
        closeable: true,
      },
    }),
  );

  dispatch(swapExecutionSuccess({ tileId }));

  setTimeout(() => {
    dispatch(swapExecutionReseted({ tileId, userPreferences: getState().userPreferences }));

    // TODO: can be not a Swap tile if the user doesn't have access to Swap
    const resetedTile = selectSwapTile(getState(), tileId);

    if (isNotDefined(resetedTile) || isNotDefined(resetedTile.tradeCapture)) {
      return;
    }

    void getSwapTradeCaptureQueue(tileId).add(async () => {
      await dispatch(
        // @ts-expect-error ok, it is only for NDS
        swapApi.endpoints.checkSwapByTradeCapture.initiate({
          tileId: tileId,
          isNonDeliverable: resetedTile.isNonDeliverable,
          properties: {
            ...resetedTile.properties,
            nearNotional: resetedTile.properties.nearNotional ?? null,
            nearNotionalCurrencyIndex: resetedTile.properties.nearNotionalCurrencyIndex ?? null,
            nearExpiryDate: resetedTile.properties.nearExpiryDate ?? resetedTile.properties.nearExpiryDateTenor ?? 'SP',
            nearPrice: null,
            farNotional: resetedTile.properties.farNotional ?? null,
            farNotionalCurrencyIndex: resetedTile.properties.farNotionalCurrencyIndex ?? null,
            farExpiryDate: resetedTile.properties.farExpiryDate ?? null,
            farExpiryDateTenor: resetedTile.properties.farExpiryDateTenor ?? null,
            farPrice: null,
          },
        }),
      );
    });
  }, 2 * SECOND_IN_MILLIS);
};

const buildHandleReceiveDealRejected = (params: ExecuteSwapRfsParams, dispatch: AppDispatch) => {
  return (dealRejected: DealRejected) => {
    if (dealRejected.Execute.RfsId !== params.rfsId) {
      return;
    }

    const data: GrowUpdate = {
      status: 'rejected',
      errorCode: dealRejected.DealReject.ErrorCode,
      multipassErrorCode: dealRejected.DealReject.MultipassErrorCode,
      reason: dealRejected.DealReject.ErrorMessage,
      sessionId: dealRejected.DealReject.MultipassSessionId,
    };
    dispatch(GrowlSlice.actions.update({ growlId: params.rfsId, data, options: { closeable: true } }));
    dispatchSwapExecutionRejected(params.tileId, params.rfsId, dispatch);
  };
};

const dispatchSwapExecutionRejected = (tileId: string, rfsId: string, dispatch: AppDispatch) => {
  dispatch(swapApi.internalActions.removeMutationResult({ fixedCacheKey: rfsId }));
  dispatch(swapExecutionRejected({ tileId }));
  setTimeout(() => dispatch(swapRejectedExecutionReseted({ tileId })), 2 * SECOND_IN_MILLIS);
};

const buildHandleReceiveFillReport = (params: ExecuteSwapRfsParams, dispatch: AppDispatch) => {
  const updateGrowl = buildHandleLateFillReport(params.rfsId, dispatch);

  return (fillReport: FillReport) => {
    if (fillReport.Execute.RfsId !== params.rfsId) {
      return;
    }

    updateGrowl(fillReport);

    dispatch(swapApi.internalActions.removeMutationResult({ fixedCacheKey: params.rfsId }));

    dispatch(swapExecutionSuccess({ tileId: params.tileId }));
  };
};
